export function formatDate(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateLong(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatMoney(cents?: number, currency = "usd"): string {
  if (cents == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * Convert Firestore Timestamp-ish values to JS Date.
 *
 * Handles every shape we've seen in practice:
 *   - Real `Date` objects (already converted upstream).
 *   - Web SDK Timestamps with `.seconds` and `.nanoseconds`.
 *   - Admin SDK Timestamps that came over the callable wire as JSON, which
 *     emit `_seconds` / `_nanoseconds` (with leading underscores).
 *   - Anything with a `.toDate()` method (defensive).
 *   - ISO strings.
 *
 * Returns null when the input is empty, malformed, or unrecognized so
 * callers can treat "no timestamp" as a single null check.
 */
export function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === "string") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v !== "object" || v === null) return null;

  // Web SDK Timestamp shape: { seconds, nanoseconds }
  if ("seconds" in v && typeof (v as { seconds?: number }).seconds === "number") {
    return new Date((v as { seconds: number }).seconds * 1000);
  }
  // Admin SDK toJSON shape: { _seconds, _nanoseconds }
  if (
    "_seconds" in v &&
    typeof (v as { _seconds?: number })._seconds === "number"
  ) {
    return new Date((v as { _seconds: number })._seconds * 1000);
  }
  // Last-ditch: any object with a toDate() method (rare on wire but cheap).
  const maybe = v as { toDate?: () => Date };
  if (typeof maybe.toDate === "function") {
    try {
      const d = maybe.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  return null;
}
